"use client";
import { FC, useContext } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { Button } from "@/components/Button";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import cn from "classnames";
import { useSearchResults } from "./SWDCInternalSearchWrapper";
import { CscInternalSearchContext } from "@/libs/builder/blocks/InternalSearch/CscInternalSearchContextBlock";

export type SearchHit = {
  title: string;
  highlights: string;
  link: string;
};

export type RecommendedProduct = {
  longName: string;
  tagline: string;
  productInformationURL: string;
};

export type BannerProduct = {
  shortName: string;
  longName: string;
  tagline: string;
  onlineQuoteProductDescription: string;
  productInformationURL: string;
  registrationFormURL: string;
  demoURL: string | null;
  productCategories: string;
  thumbnailImageModel: {
    src: string;
    alt: string;
  };
};

export type SearchResults = {
  hits: SearchHit[];
  totalHits: number;
  recommendedProduct?: RecommendedProduct;
  bannerProduct?: BannerProduct | null;
  nextPage?: number;
  previousPage?: number;
};

type InternalSearchResultsProps = {
  variant: "swdc" | "support";
  searchValue?: string;
  resultsText?: string;
  loadingText?: string;
  previousButtonText?: string;
  previousButtonDLD?: string;
  previousButtonDLT?: string;
  nextButtonText?: string;
  nextButtonDLD?: string;
  nextButtonDLT?: string;
  noResultsTitle?: string;
  noResultsSuggestion?: string;
};

export const InternalSearchResults: FC<InternalSearchResultsProps> = ({
  variant,
  resultsText,
  loadingText,
  previousButtonText,
  previousButtonDLD,
  previousButtonDLT,
  nextButtonText,
  nextButtonDLD,
  nextButtonDLT,
  noResultsTitle,
  noResultsSuggestion,
}) => {
  const {
    searchResults: swdcSearchResults,
    isLoading: swdcIsLoading,
    searchQuery: swdcSearchQuery,
  } = useSearchResults();

  const {
    searchExpression: cscSearchQuery,
    currentPage: cscPageNumber,
    searchResults: cscSearchResults,
    isLoading: cscIsLoading,
    onGoToNextPage,
    onGoToPreviousPage,
  } = useContext(CscInternalSearchContext);

  const searchQuery = variant === "support" ? cscSearchQuery : swdcSearchQuery;
  const searchResults =
    variant === "support" ? cscSearchResults : swdcSearchResults;
  const isLoading = variant === "swdc" ? swdcIsLoading : cscIsLoading;

  const searchParams = useSearchParams();
  const pathname = usePathname();

  const pageNumber =
    variant === "support"
      ? cscPageNumber
      : parseInt(searchParams?.get("page") || "1", 10);

  const getPageHref = (page: number) => {
    const params = new URLSearchParams(searchParams?.toString() || "");
    params.set("page", page.toString());
    return `${pathname}?${params.toString()}`;
  };

  if (!searchQuery) {
    return null;
  }

  if (isLoading) {
    return loadingText;
  }

  const hasNoResults = !searchResults || searchResults.totalHits === 0;

  if (hasNoResults) {
    // Generate content from noResultsTitle and noResultsSuggestion
    if (noResultsTitle) {
      const stripped = noResultsTitle.replace("{searchValue}", "");
      const keywordIndex = noResultsTitle.indexOf("{searchValue}");
      const [firstPart, secondPart] = [
        stripped.slice(0, keywordIndex),
        stripped.slice(keywordIndex),
      ];

      return (
        <>
          <p className="text-body-m text-swdc-black-600 mb-4">
            {firstPart}
            <strong>{searchQuery}</strong>
            {secondPart}
          </p>
          {noResultsSuggestion && (
            <div
              className="builder-text text-body-m text-swdc-gray-400"
              dangerouslySetInnerHTML={{ __html: noResultsSuggestion }}
            />
          )}
        </>
      );
    }

    return null;
  }

  if (!searchResults) {
    return null;
  }

  const results = searchResults;

  const renderResultsText = () => {
    if (!resultsText) return null;

    const countStr = results.totalHits.toString();
    const stripped = resultsText.replace("{count}", "");
    const keywordIndex = resultsText.indexOf("{count}");
    const [firstPart, secondPart] = [
      stripped.slice(0, keywordIndex),
      stripped.slice(keywordIndex),
    ];

    return (
      <>
        {firstPart}
        <strong>{countStr}</strong>
        {secondPart.replace("{searchTerm}", searchQuery)}
      </>
    );
  };

  const RESULTS_PER_PAGE = 10;

  // The API returns `previousPage` number even when there's no previous page,
  // so we have to check whether the current page number is higher than `1`.
  const hasPreviousPage = !!results?.previousPage && pageNumber > 1;
  // The API returns `nextPage` number even when there's no next page.
  // Therefore, we have to check using `totalHits` property:
  // We know that there are always 10 results per page, so we check whether
  // the `current page` * `results per page` is lower than `total results` (hits).
  const hasNextPage =
    !!results?.nextPage &&
    (results.nextPage - 1) * RESULTS_PER_PAGE < results.totalHits;

  return (
    <div
      {...getDataAutomationId("search-results")}
      className="flex flex-col gap-8"
    >
      {(loadingText || resultsText) && (
        <div className="text-body-s text-headline-eyebrow">
          {isLoading ? loadingText : renderResultsText()}
        </div>
      )}

      <div className="flex flex-col gap-5">
        {results.recommendedProduct && (
          <div className="flex flex-col gap-2">
            <Button
              as="a"
              href={results.recommendedProduct?.productInformationURL}
              variant="tertiary"
              icon="none"
              className="text-subhead-2xs! h-auto min-h-0 min-w-0 font-bold"
              {...getDataLinkAttributes({
                dlt: "resultLink",
                dld: results.recommendedProduct?.longName,
              })}
            >
              {results.recommendedProduct?.longName}
            </Button>
            <div className="text-body-s sm:text-body-m text-body-primary wrap-anywhere-text">
              <span
                dangerouslySetInnerHTML={{
                  __html: results.recommendedProduct?.tagline || "",
                }}
              />
              {" --- "}
              <b>{searchQuery}</b>
            </div>
          </div>
        )}

        {results.hits.map((hit, index) => (
          <div key={`${hit.title}-${index}`} className="flex flex-col gap-2">
            <Button
              as="a"
              size="m"
              href={hit.link}
              variant="tertiary"
              icon="none"
              className="text-subhead-2xs! h-auto min-h-0 min-w-0 font-bold"
              {...getDataLinkAttributes({
                dlt: "resultLink",
                dld: hit.title,
              })}
            >
              {hit.title}
            </Button>
            {hit?.highlights && (
              <div
                className="text-body-s sm:text-body-m text-body-primary wrap-anywhere-text"
                dangerouslySetInnerHTML={{ __html: hit.highlights }}
              />
            )}
          </div>
        ))}
      </div>

      {results.totalHits > RESULTS_PER_PAGE &&
        (hasPreviousPage || hasNextPage) && (
          <div
            {...getDataAutomationId("search-pagination")}
            className={cn("flex flex-row", {
              "justify-between": hasPreviousPage && hasNextPage,
              "justify-end": !hasPreviousPage && hasNextPage,
              "justify-start": hasPreviousPage && !hasNextPage,
            })}
          >
            {hasPreviousPage && (
              <Button
                href={
                  variant === "swdc"
                    ? getPageHref(results.previousPage!)
                    : undefined
                }
                onClick={
                  variant === "support" ? () => onGoToPreviousPage() : undefined
                }
                variant="tertiary"
                icon="circleArrowLeft"
                iconPosition="left"
                {...getDataLinkAttributes({
                  dlt: previousButtonDLT,
                  dld: previousButtonDLD,
                })}
              >
                {previousButtonText}
              </Button>
            )}
            {hasNextPage && (
              <Button
                href={
                  variant === "swdc"
                    ? getPageHref(results.nextPage!)
                    : undefined
                }
                onClick={
                  variant === "support" ? () => onGoToNextPage() : undefined
                }
                variant="tertiary"
                icon="circleArrowRight"
                iconPosition="right"
                {...getDataLinkAttributes({
                  dlt: nextButtonDLT,
                  dld: nextButtonDLD,
                })}
              >
                {nextButtonText}
              </Button>
            )}
          </div>
        )}
    </div>
  );
};
